import type { Stn, StnBySkuRow } from '@api/warehouse-ops';
import type { DataSheetColumn } from '@components/ui/DataSheet';
import { Group, Text } from '@mantine/core';
import { formatCount } from '@utils/currency';
import { format } from 'date-fns';

import { StatusBadge } from '../../procurementOrders/shared';

import classes from './stnColumns.module.css';
import StnStatusBadge from './StnStatusBadge';
import { SOURCE_LABEL, STATUS_LABEL, mirroredStatusLabel } from './stnLabels';
import type { StnNextStep } from './useStnActions';

/** Rarely informative columns ship hidden; the columns menu brings them back. */
export const DEFAULT_HIDDEN_COLUMNS = ['itemType', 'workOrderId'];

export const COLUMN_DEFS = [
    { key: 'fromNodeId', label: 'From' },
    { key: 'toNodeId', label: 'To' },
    { key: 'source', label: 'Source' },
    { key: 'status', label: 'Status' },
    { key: 'totalPlannedQty', label: 'Units planned' },
    { key: 'totalDispatchedQty', label: 'Units dispatched' },
    { key: 'totalReceivedQty', label: 'Units received' },
    { key: 'plannedDispatchDate', label: 'Ship by' },
    { key: 'dispatchedAt', label: 'Dispatched' },
    { key: 'expectedArrivalDate', label: 'Expected arrival' },
    { key: 'receivedAt', label: 'Received' },
];
export const OPTIONAL_COLUMN_DEFS = [
    { key: 'itemType', label: 'Item Type' },
    { key: 'workOrderId', label: 'Work Order' },
];

/** "10 Oct 2026" — the date style the other operational grids use. */
export const formatDate = (value: string | null | undefined): string =>
    value
        ? new Date(value).toLocaleDateString('en-GB', {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
          })
        : '—';

/** "—" until a line carries the figure, so "not dispatched yet" reads apart from 0. */
export const formatUnits = (value: number | null | undefined): string =>
    value === null || value === undefined ? '—' : formatCount(value);

/**
 * The Notes sheet's columns, in full. The list hides what the columns menu says to; the SKU
 * panel drops the ones every row would repeat.
 */
export const buildStnColumns = (): DataSheetColumn<Stn>[] => [
    {
        // Same identity-column treatment as the Inventory grids: wide, sticky, the code in
        // mono (it wraps — gate-pass codes run long), and a muted second line.
        key: 'code',
        header: 'STN Code',
        width: 240,
        sticky: true,
        captureDetailHeader: 'Transfer date',
        valueAccessor: (row) => row.code,
        render: (_, row) => (
            <span className="flex min-w-0 flex-col leading-tight">
                <span className="break-all font-mono text-xs text-slate-600">{row.code}</span>
                <Text size="11px" c="dimmed">
                    {formatDate(row.dispatchedAt ?? row.createdAt)}
                </Text>
            </span>
        ),
    },
    {
        key: 'fromNodeId',
        header: 'From',
        width: 190,
        valueAccessor: (row) => row.fromNodeName ?? row.fromNodeId ?? '',
        render: (_, row) => row.fromNodeName ?? (row.fromNodeId || '—'),
    },
    {
        key: 'toNodeId',
        header: 'To',
        width: 210,
        valueAccessor: (row) => row.toNodeName ?? row.toNodeId,
        render: (_, row) => (
            <Group gap={6} wrap="nowrap">
                <span>{row.toNodeName ?? row.toNodeId}</span>
                {row.toNodeKind && row.toNodeKind !== 'INTERNAL' && (
                    <Text size="11px" c="dimmed">
                        {row.toNodeKind === 'VENDOR' ? 'vendor' : 'channel'}
                    </Text>
                )}
            </Group>
        ),
    },
    {
        key: 'source',
        header: 'Source',
        width: 170,
        valueAccessor: (row) => SOURCE_LABEL[row.source ?? ''] ?? row.source ?? '',
        // Neutral chip in the shared shape; only STNs raised here are tinted, since
        // they are the only ones with actions behind them.
        render: (_, row) => (
            <StatusBadge
                status={row.source}
                styleOverride={{
                    ...(row.source === 'PLATFORM'
                        ? { color: 'rgb(124, 95, 240)', bg: 'rgba(144, 117, 255, 0.16)' }
                        : { color: 'rgb(100, 116, 139)', bg: 'rgba(100, 116, 139, 0.12)' }),
                    label: SOURCE_LABEL[row.source ?? ''] ?? row.source ?? '—',
                }}
            />
        ),
    },
    {
        key: 'status',
        header: 'Status',
        width: 120,
        valueAccessor: (row) => STATUS_LABEL[row.status],
        render: (_, row) => <StnStatusBadge status={row.status} label={mirroredStatusLabel(row)} />,
    },
    {
        // What the note was raised for. Dispatched and Received fill in beside it as the
        // stock leaves and lands, so the three read as one flow.
        key: 'totalPlannedQty',
        header: 'Units planned',
        width: 130,
        align: 'right',
        valueAccessor: (row) => row.totalPlannedQty ?? null,
        render: (_, row) => formatUnits(row.totalPlannedQty),
    },
    {
        // Summed over the STN's SKU lines; clicking the row opens the line-by-line split.
        key: 'totalDispatchedQty',
        header: 'Units dispatched',
        width: 130,
        align: 'right',
        valueAccessor: (row) => row.totalDispatchedQty ?? null,
        render: (_, row) => formatUnits(row.totalDispatchedQty),
    },
    {
        key: 'totalReceivedQty',
        header: 'Units received',
        width: 130,
        align: 'right',
        valueAccessor: (row) => row.totalReceivedQty ?? null,
        render: (_, row) => formatUnits(row.totalReceivedQty),
    },
    {
        // The four dates read left to right as the note's timeline: when it is to leave,
        // when it left, when it is due, when it landed. On an Amazon note "Ship by" is
        // the declared ship date (own carrier) or the booked pickup day (partnered), and
        // the arrival is the FC delivery appointment, as on the detail panel.
        key: 'plannedDispatchDate',
        header: 'Ship by',
        width: 120,
        render: (_, row) => formatDate(row.plannedDispatchDate),
    },
    {
        key: 'dispatchedAt',
        header: 'Dispatched',
        width: 120,
        render: (_, row) => formatDate(row.dispatchedAt),
    },
    {
        key: 'expectedArrivalDate',
        header: 'Expected arrival',
        width: 130,
        render: (_, row) => formatDate(row.expectedArrivalDate),
    },
    {
        key: 'receivedAt',
        header: 'Received',
        width: 120,
        render: (_, row) => formatDate(row.receivedAt),
    },
    {
        key: 'itemType',
        header: 'Item Type',
        width: 120,
        render: (_, row) => (row.itemType === 'product' ? 'Finished Good' : 'Raw / PM'),
    },
    {
        key: 'workOrderId',
        header: 'Work Order',
        width: 140,
        render: (_, row) => row.workOrderId ?? '—',
    },
];

export type StnRowActions = {
    /** The row's next step (stnNextStep with whether it has a page to be received on). */
    nextStepFor: (row: Stn) => StnNextStep | null;
    onStep: (step: StnNextStep, row: Stn) => void;
    /** The step in flight and the row it is for, so only that row's button spins. */
    pending: { step: StnNextStep; stnId: string } | null;
};

const NEXT_STEP_BUTTON: Record<StnNextStep, { label: string; className: string }> = {
    APPROVE: { label: 'Approve', className: classes.approve },
    DISPATCH: { label: 'Dispatch', className: classes.dispatch },
    RECEIVE: { label: 'Receive', className: classes.receive },
};

/**
 * The Notes sheet's next-step column: one button for the step that moves a transfer forward
 * (Approve a draft, Dispatch an approved note, Receive one on its way to a page it is received
 * on), the same steps as the side panel. Cancel stays in the panel, away from a mis-click in a dense grid. It is not part
 * of buildStnColumns, so the download (allColumns), the columns menu and the SKU panel never
 * carry it.
 */
export const buildStnNextStepColumn = ({ nextStepFor, onStep, pending }: StnRowActions): DataSheetColumn<Stn> => ({
    key: '_nextStep',
    header: 'Next step',
    width: 110,
    // A button, not a value: nothing to sort or filter by.
    sortable: false,
    filterable: false,
    // The step's name as the cell's value (blank when there is none), for anything that reads
    // the column as data. The Add to chat snapshot reads the button's text, which is the same.
    valueAccessor: (row) => {
        const step = nextStepFor(row);
        return step ? NEXT_STEP_BUTTON[step].label : '';
    },
    render: (_, row) => {
        const step = nextStepFor(row);
        if (!step) return null;
        const { label, className } = NEXT_STEP_BUTTON[step];
        const busy = pending?.stnId === row.id && pending.step === step;
        return (
            // Chip-sized, to sit level with the Source and Status chips beside it.
            <span className={classes.stepCell}>
                <button
                    type="button"
                    className={`${classes.stepButton} ${className}`}
                    disabled={busy}
                    onClick={(event) => {
                        // The button acts on the row; a click on it must not also open the side panel.
                        event.stopPropagation();
                        onStep(step, row);
                    }}
                >
                    {busy ? '…' : label}
                </button>
            </span>
        );
    },
});

/**
 * The By SKU sheet: one row per SKU over its open notes. The three unit columns are the
 * Notes sheet's, summed; Open is what is still to land; the two dates are the next ones due.
 */
export const buildSkuColumns = (): DataSheetColumn<StnBySkuRow>[] => {
    const today = format(new Date(), 'yyyy-MM-dd');
    return [
        {
            key: 'itemId',
            header: 'SKU',
            width: 260,
            sticky: true,
            captureDetailHeader: 'Name',
            valueAccessor: (row) => row.itemId,
            render: (_, row) => (
                <span className="flex min-w-0 flex-col leading-tight">
                    <span className="font-medium">{row.itemId}</span>
                    <Text size="11px" c="dimmed" truncate>
                        {row.itemName ?? ''}
                    </Text>
                </span>
            ),
        },
        {
            key: 'noteCount',
            header: 'Open notes',
            width: 110,
            align: 'right',
            valueAccessor: (row) => row.noteCount,
            render: (_, row) => (
                <Text size="sm" c="blue.7" fw={500}>
                    {row.noteCount} {row.noteCount === 1 ? 'note' : 'notes'} ›
                </Text>
            ),
        },
        {
            key: 'plannedQty',
            header: 'Units planned',
            width: 130,
            align: 'right',
            valueAccessor: (row) => row.plannedQty,
            render: (_, row) => formatUnits(row.plannedQty),
        },
        {
            key: 'dispatchedQty',
            header: 'Units dispatched',
            width: 130,
            align: 'right',
            valueAccessor: (row) => row.dispatchedQty,
            render: (_, row) => formatUnits(row.dispatchedQty),
        },
        {
            key: 'receivedQty',
            header: 'Units received',
            width: 130,
            align: 'right',
            valueAccessor: (row) => row.receivedQty,
            render: (_, row) => formatUnits(row.receivedQty),
        },
        {
            key: 'openQty',
            header: 'Open',
            width: 110,
            align: 'right',
            valueAccessor: (row) => row.openQty,
            render: (_, row) => formatUnits(row.openQty),
        },
        {
            // A ship-by already in the past on a note that has not left reads as overdue.
            key: 'nextShipBy',
            header: 'Next ship by',
            width: 130,
            render: (_, row) =>
                row.nextShipBy && row.nextShipBy < today ? (
                    <Text size="sm" c="orange.8" title="Ship-by date has passed and the note has not left">
                        {formatDate(row.nextShipBy)}
                    </Text>
                ) : (
                    formatDate(row.nextShipBy)
                ),
        },
        {
            key: 'nextExpectedArrival',
            header: 'Next expected arrival',
            width: 160,
            render: (_, row) => formatDate(row.nextExpectedArrival),
        },
    ];
};
